import './GamblingTableView.css';
import { DispatchCardDismiss } from './DispatchCardDismiss';

/**
 * V9.2.9 (2026-06-06) — GamblingTableView.
 *
 * Pure-presentation render of a `force_gambling_play` dispatch action.
 * Ch 7 casino round: 4 card games + roulette + optional cheating
 * detection.
 *
 * Schema 1-to-1 with the V9.1.c tool args at routes/dispatch.py:815-879:
 *   - game_type: enum {chemin_de_fer, baccarat, blackjack, poker, roulette}
 *   - bet_amount: integer ≥1 (currency unit per scenario context)
 *   - roulette_bet_type: enum (required for roulette, null otherwise) —
 *     single_number / red_black / odd_even / high_low / column / dozen /
 *     split / street / corner / six_line / five_number
 *   - attempting_cheat: boolean (engine rolls cheating-detection EF vs
 *     NPC dealer Skill Level)
 *   - rationale: GM voice-line ≤30 words (optional)
 *
 * Surfaces SETUP. Resolved win/loss/push + payout surface via optional
 * props.
 */

export type GamblingGameType =
  | 'chemin_de_fer'
  | 'baccarat'
  | 'blackjack'
  | 'poker'
  | 'roulette';

export type RouletteBetType =
  | 'single_number'
  | 'red_black'
  | 'odd_even'
  | 'high_low'
  | 'column'
  | 'dozen'
  | 'split'
  | 'street'
  | 'corner'
  | 'six_line'
  | 'five_number';

export type GamblingOutcome = 'win' | 'loss' | 'push';

/**
 * §3.F (2026-06-10) — optional venue context from the Thrilling Locations
 * VG35010 fold. When the gambling round happens at a known casino
 * (Casino de Monte Carlo / London casino, served by
 * /api/modules/briefing-room/thrilling-cities/casinos), the renderer passes
 * the casino name + currency + relevant table bet-limits to surface them as
 * reference. Resolution itself stays canonical Ch 7 (these are colour, not a
 * resolver) — see audit §3.C.
 */
export interface GamblingCasinoContext {
  name: string;
  currency: string;
  betLimits: Array<{ label: string; min?: number; max?: number }>;
}

export interface GamblingTableViewProps {
  gameType: GamblingGameType;
  betAmount: number;
  /** Required when gameType=roulette; ignored otherwise. */
  rouletteBetType?: RouletteBetType | null;
  attemptingCheat?: boolean;
  /**
   * §3.F — optional Thrilling Locations casino venue + bet-limits reference.
   * SCAFFOLDING / NOT WIRED IN PRODUCTION: ScenarioRunner's force_gambling_play
   * render (the only callsite) does NOT pass this — the scenario has no
   * casino-in-scene signal yet. The render path + tests exist so the surface is
   * ready; populating it needs a future casino-venue signal. (G1-reviewer flagged
   * the unwired-scaffolding risk; this note keeps the next reader from assuming
   * it renders end-to-end today.)
   */
  casinoContext?: GamblingCasinoContext | null;
  rationale?: string;
  resolvedOutcome?: GamblingOutcome | null;
  /** Resolved net payout (positive=win, negative=loss, 0=push). */
  resolvedPayout?: number | null;
  /** V9.2.x.3: when provided, renders a ✕ dismiss button (top-right). */
  onDismiss?: () => void;
  testidPrefix?: string;
}

const GAME_LABEL: Record<GamblingGameType, string> = {
  chemin_de_fer: 'Chemin de Fer',
  baccarat: 'Baccarat',
  blackjack: 'Blackjack',
  poker: 'Poker',
  roulette: 'Roulette',
};

const ROULETTE_BET_LABEL: Record<RouletteBetType, string> = {
  single_number: 'Single Number',
  red_black: 'Red/Black',
  odd_even: 'Odd/Even',
  high_low: 'High/Low',
  column: 'Column',
  dozen: 'Dozen',
  split: 'Split',
  street: 'Street',
  corner: 'Corner',
  six_line: 'Six Line',
  five_number: 'Five Number',
};

const OUTCOME_LABEL: Record<GamblingOutcome, string> = {
  win: 'Win',
  loss: 'Loss',
  push: 'Push',
};

function formatPayout(payout: number): string {
  if (payout === 0) return '0';
  return payout > 0 ? `+${payout}` : `${payout}`;
}

export function GamblingTableView({
  gameType,
  betAmount,
  rouletteBetType,
  attemptingCheat = false,
  casinoContext,
  rationale,
  resolvedOutcome,
  resolvedPayout,
  onDismiss,
  testidPrefix = 'br-gambling',
}: GamblingTableViewProps) {
  const isRoulette = gameType === 'roulette';
  const showRouletteBet =
    isRoulette &&
    rouletteBetType !== undefined &&
    rouletteBetType !== null &&
    rouletteBetType in ROULETTE_BET_LABEL;
  const hasOutcome =
    resolvedOutcome === 'win' ||
    resolvedOutcome === 'loss' ||
    resolvedOutcome === 'push';
  const hasPayout = typeof resolvedPayout === 'number';

  return (
    <aside
      className="br-gambling"
      data-testid={testidPrefix}
      data-game-type={gameType}
      data-attempting-cheat={attemptingCheat ? 'true' : 'false'}
      data-state={hasOutcome ? 'resolved' : 'pending'}
    >
      <DispatchCardDismiss onDismiss={onDismiss} testidPrefix={testidPrefix} />
      <header className="br-gambling-header">
        <span className="br-gambling-label">Gambling</span>
        <span
          className="br-gambling-game-chip"
          data-testid={`${testidPrefix}-game-chip`}
          data-game-id={gameType}
        >
          {GAME_LABEL[gameType]}
        </span>
      </header>

      <div
        className="br-gambling-bet-row"
        data-testid={`${testidPrefix}-bet-row`}
      >
        <span className="br-gambling-bet-label">Bet</span>
        <span
          className="br-gambling-bet-value br-label"
          data-testid={`${testidPrefix}-bet-amount`}
        >
          {betAmount}
        </span>
      </div>

      {showRouletteBet && (
        <div
          className="br-gambling-roulette-bet"
          data-testid={`${testidPrefix}-roulette-bet`}
          data-bet-type={rouletteBetType}
        >
          <span className="br-gambling-roulette-bet-label">Roulette Bet</span>
          <span className="br-gambling-roulette-bet-value">
            {ROULETTE_BET_LABEL[rouletteBetType as RouletteBetType]}
          </span>
        </div>
      )}

      {attemptingCheat && (
        <p
          className="br-gambling-cheat-warn"
          data-testid={`${testidPrefix}-cheat-warn`}
        >
          Cheating — dealer rolls detection EF.
        </p>
      )}

      {casinoContext &&
        casinoContext.betLimits &&
        casinoContext.betLimits.length > 0 && (
          <div
            className="br-gambling-venue"
            data-testid={`${testidPrefix}-venue`}
            data-casino={casinoContext.name}
          >
            <div className="br-gambling-venue-header">
              <span className="br-gambling-venue-name">{casinoContext.name}</span>
              <span className="br-gambling-venue-currency br-label">
                {casinoContext.currency}
              </span>
            </div>
            <ul className="br-gambling-venue-limits">
              {casinoContext.betLimits.map((bl, i) => (
                <li
                  key={`${bl.label}-${i}`}
                  className="br-gambling-venue-limit-row"
                  data-testid={`${testidPrefix}-venue-limit-${i}`}
                >
                  <span className="br-gambling-venue-limit-label">{bl.label}</span>
                  <span className="br-gambling-venue-limit-range br-label">
                    {bl.min !== undefined ? bl.min : '—'}
                    {' – '}
                    {bl.max !== undefined ? bl.max : '—'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

      {hasOutcome ? (
        <div
          className="br-gambling-resolved"
          data-testid={`${testidPrefix}-resolved`}
          data-outcome={resolvedOutcome}
        >
          <span
            className="br-gambling-outcome-chip"
            data-testid={`${testidPrefix}-outcome-chip`}
            data-outcome-id={resolvedOutcome}
          >
            {OUTCOME_LABEL[resolvedOutcome as GamblingOutcome]}
          </span>
          {hasPayout && (
            <span
              className="br-gambling-payout br-label"
              data-testid={`${testidPrefix}-payout`}
              data-payout-sign={resolvedPayout > 0 ? 'positive' : resolvedPayout < 0 ? 'negative' : 'zero'}
            >
              {formatPayout(resolvedPayout)}
            </span>
          )}
        </div>
      ) : (
        <p
          className="br-gambling-pending"
          data-testid={`${testidPrefix}-pending`}
        >
          Awaiting deal/spin…
        </p>
      )}

      {rationale !== undefined && rationale.length > 0 && (
        <p
          className="br-gambling-rationale"
          data-testid={`${testidPrefix}-rationale`}
        >
          {rationale}
        </p>
      )}
    </aside>
  );
}
